import React, { useEffect, useRef, useState } from "react";
import { FileText, Eye, ChevronDown, RefreshCw } from "lucide-react";

/**
 * Uložené PDF nabídky — otevře poslední vygenerované PDF bez nového generování.
 * `pdf` = {s_cenami: {url, vytvoreno, aktualni} | null, bez_cen: … | null} ze serveru.
 * Když se nabídka od vygenerování změnila (třeba po návratu na rozpracovanou),
 * hlavní akcí je „Aktualizovat PDF" — starý náhled zůstává v nabídce tlačítka.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const kdy = (d) => (d ? new Date(d).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const popisek = (p) => (p.aktualni ? `PDF z ${kdy(p.vytvoreno)}` : `PDF z ${kdy(p.vytvoreno) || "dřívějška"} — nabídka se od té doby změnila`);

/** Ikona do řádku seznamu. */
export function PdfIkona({ pdf, testid }) {
  const p = pdf?.s_cenami || pdf?.bez_cen;
  if (!p) return <span className="text-muted-2 text-[12px]" aria-label="PDF zatím nevygenerované">—</span>;
  return (
    <a href={`${API_BASE}${p.url}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
      title={popisek(p)} aria-label={`Otevřít PDF. ${popisek(p)}`} data-testid={testid}
      className="tap md:min-h-0 md:min-w-0 relative h-9 w-9 rounded-full inline-flex items-center justify-center text-sec hover:bg-white hover:text-ink border border-transparent hover:border-line-strong transition-colors">
      <FileText size={17} strokeWidth={1.9} />
      {!p.aktualni && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#e0901a] ring-2 ring-white" />}
    </a>
  );
}

/** Tlačítko „Náhled PDF" do detailu nabídky. `onExport(sCenami)` vytvoří nové PDF. */
export function PdfNahledTlacitko({ pdf, tmavy = false, onExport, busy = false }) {
  const [menu, setMenu] = useState(false);
  const obal = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const pryc = (e) => { if (!obal.current?.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", pryc);
    return () => document.removeEventListener("mousedown", pryc);
  }, [menu]);
  const moznosti = [["S cenami položek", pdf?.s_cenami, true], ["Bez cen položek", pdf?.bez_cen, false]].filter(([, p]) => p);
  if (!moznosti.length) return null;
  const otevri = (p) => { window.open(`${API_BASE}${p.url}`, "_blank", "noopener"); setMenu(false); };
  const vytvor = (sCenami) => { setMenu(false); onExport(sCenami); };
  const zastarale = moznosti.filter(([, p]) => !p.aktualni);
  // Nabídka se od PDF změnila → hlavní je nové PDF, starý náhled by klamal.
  const aktualizovat = !!onExport && zastarale.length > 0;
  const klik = () => {
    if (moznosti.length > 1) { setMenu((v) => !v); return; }
    if (aktualizovat) vytvor(moznosti[0][2]); else otevri(moznosti[0][1]);
  };
  return (
    <div className="relative" ref={obal}>
      <button onClick={klik} disabled={busy}
        title={moznosti.length === 1 ? popisek(moznosti[0][1]) : undefined} data-testid="pdf-nahled"
        className={tmavy
          ? "pill relative h-10 px-4 inline-flex items-center gap-1.5 text-[13px] font-700 bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.16)] hover:bg-white/[.16] disabled:opacity-60"
          : "btn-ghost h-9 px-3 text-[12.5px] relative disabled:opacity-60"}>
        {aktualizovat ? <RefreshCw size={15} /> : <Eye size={15} />}
        {busy ? "Připravuji PDF…" : aktualizovat ? "Aktualizovat PDF" : "Náhled PDF"}
        {moznosti.length > 1 && <ChevronDown size={14} className="-mr-0.5" />}
        {zastarale.length > 0 && <span className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#e0901a] ring-2 ${tmavy ? "ring-ink" : "ring-white"}`} />}
      </button>
      {menu && (
        <div className="vyber-menu absolute right-0 mt-1 z-40 text-ink bg-white rounded-card shadow-panel border border-line-softer p-1 w-[260px]" data-testid="pdf-nahled-menu">
          {aktualizovat && zastarale.map(([nazev, , sCenami]) => (
            <button key={`nove-${nazev}`} onClick={() => vytvor(sCenami)} data-testid={`pdf-aktualizovat-${sCenami ? "s-cenami" : "bez-cen"}`}
              className="vyber-polozka w-full text-left px-3 py-2 rounded-[8px] flex items-center gap-2 text-[12.5px] font-700 text-accent-hover">
              <RefreshCw size={14} /> Nové PDF — {nazev.toLowerCase()}
            </button>
          ))}
          {moznosti.map(([nazev, p]) => (
            <button key={nazev} onClick={() => otevri(p)} className="vyber-polozka w-full text-left px-3 py-2 rounded-[8px] flex flex-col">
              <span className="text-[12.5px] font-600 text-ink">{nazev}</span>
              <span className={`text-[11.5px] ${p.aktualni ? "text-muted" : "text-[#a0610c]"}`}>{popisek(p)}</span>
            </button>
          ))}
        </div>
      )}
      {zastarale && moznosti.length === 1 && (
        <span className="sr-only">PDF je starší než poslední úprava nabídky.</span>
      )}
    </div>
  );
}
